import TopBanner from "@/components/Banner/TopBanner";
import FeaturesGrid from "@/components/Home/FeaturesGrid/FeaturesGrid";
import GraphyAssist from "@/components/Home/GraphyAssist";
import Hero from "@/components/Home/Hero";
import { HomeBottomBanner } from "@/components/Home/HomeBottomBanner";
import ProductStatistics from "@/components/Home/ProductStatistics";
import HomeV2 from "@/components/Home/v2/HomeV2";
import HomeMarqueeSection, {
  HomeMarqueeSectionPropType,
} from "@/components/organisms/Carousels/HomeMarqueeSection";
import { FooterDataType } from "@/components/organisms/Footer";
import GraphyHead from "@/components/organisms/GraphyHead";
import InfoAccordion from "@/components/organisms/InfoAccordion/InfoAccordion";
import { Navbar } from "@/components/organisms/Navbar";
import { DEFAULT_COUNTRY_CODE } from "@/shared/constants/common.constants";
import {
  creatorStoriesPageData_en,
  creatorStoriesPageData_us,
} from "@/shared/data/creator-stories.data";
import { creatorStoriesPageData_kr } from "@/shared/data/creator-stories.data.kr";
import { footerData_en, footerData_us } from "@/shared/data/footer.data";
import { footerData_kr } from "@/shared/data/footer.data.kr";
import { HomePageDataType, homePageData_en } from "@/shared/data/home.data";
import { homePageData_kr } from "@/shared/data/home.data.kr";
import { homePageData_us } from "@/shared/data/home.data.us";
import { homeV2Data, HomeV2DataType } from "@/shared/data/home.v2.data";
import { homeV2DataUs } from "@/shared/data/home.v2.data.us";
import {
  NavbarDataType,
  navbarData_en,
  navbarData_us,
} from "@/shared/data/navbar.data";
import { navbarData_kr } from "@/shared/data/navbar.data.kr";
import { useDataStore } from "@/shared/store/useDataStore";
import axios from "axios";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import nookies from "nookies";

interface BannerData {
  type: string;
  isCTA: boolean;
  ctaText: string;
  ctaLink: string;
  isShowBanner: boolean;
  title: string;
}

interface BannerDataObjType {
  bannerData: BannerData;
}

const Footer = dynamic(() => import("@/components/organisms/Footer"));

type HomeLocale = "en" | "kr" | "us" | "in";

export const getServerSideProps: GetServerSideProps<{
  locale: HomeLocale;
  cookieCountryCode: string;
  cookieCurrencySymbol: string;
  homeMarqueeSectionProps: HomeMarqueeSectionPropType;
  navbarData: NavbarDataType;
  // Null on en/us — those locales render <HomeV2> exclusively and don't
  // read homePageData. Stripping it cuts 6.8 MB of inlined Lottie JSON
  // (homeHeroSectionProps.lottie + mWebLottie) out of __NEXT_DATA__,
  // which was the entire reason FCP was stuck at 26 s under throttled
  // network in Lighthouse.
  homePageData: HomePageDataType | null;
  homeV2Data: HomeV2DataType | null;
  footerData: FooterDataType;
  topBannerData?: BannerDataObjType | null;
}> = async (context) => {
  const locale = ((context.locale as HomeLocale) ?? "en") as HomeLocale;

  const homePageData = {
    en: homePageData_en,
    us: homePageData_us,
    kr: homePageData_kr,
    in: homePageData_en,
  }[locale];

  const creatorStoriesPageData = {
    en: creatorStoriesPageData_en,
    us: creatorStoriesPageData_us,
    kr: creatorStoriesPageData_kr,
    in: creatorStoriesPageData_en,
  }[locale];

  const navbarData = {
    en: navbarData_en,
    us: navbarData_us,
    kr: navbarData_kr,
    in: navbarData_en,
  }[locale];

  const footerData = {
    en: footerData_en,
    us: footerData_us,
    kr: footerData_kr,
    in: footerData_en,
  }[locale];

  const { creatorStoriesHeroSectionProps } = creatorStoriesPageData;
  let topBannerData: BannerDataObjType | null = null;
  const url = `https://graphy-marketing-pages.s3.ap-south-1.amazonaws.com/json/home.us.json`;

  try {
    const response = await axios.get(url);
    if (response?.data?.data) {
      topBannerData = response.data.data;
    }
  } catch {
    // best-effort
  }

  const filteredSuccessStories =
    creatorStoriesHeroSectionProps.successStories.filter(
      (successStory) => successStory.showOn.home
    );

  const half = Math.floor(filteredSuccessStories.length / 2);
  const homeMarqueeSectionProps: HomeMarqueeSectionPropType = {
    ...homePageData.homeMarqueeSectionProps,
    successStoriesSlider1: filteredSuccessStories.slice(0, half),
    successStoriesSlider2: filteredSuccessStories.slice(half),
  };

  const v2Data =
    locale === "en" ? homeV2Data : locale === "us" ? homeV2DataUs : null;

  // For en/us, App() takes the V2 early-return and never reads
  // homePageData, so we drop it from the SSR payload entirely. That
  // strips out the 6.8 MB of inlined Lottie JSON that was tanking FCP
  // for the en/us home pages.
  const homePageDataForResponse: HomePageDataType | null =
    locale === "en" || locale === "us" ? null : homePageData;

  try {
    const cookies = nookies.get(context);
    const cookieCountryCode = cookies?.countryCode || DEFAULT_COUNTRY_CODE;
    const cookieCurrencySymbol = cookieCountryCode === "IN" ? "₹" : "$";

    return {
      props: {
        locale,
        homePageData: homePageDataForResponse,
        homeV2Data: v2Data,
        cookieCountryCode,
        cookieCurrencySymbol,
        homeMarqueeSectionProps,
        navbarData,
        footerData,
        topBannerData,
      },
    };
  } catch {
    return {
      props: {
        locale,
        homePageData: homePageDataForResponse,
        homeV2Data: v2Data,
        cookieCountryCode: "",
        cookieCurrencySymbol: "",
        homeMarqueeSectionProps,
        navbarData,
        footerData,
        topBannerData,
      },
    };
  }
};

export default function App({
  locale,
  cookieCountryCode,
  cookieCurrencySymbol,
  homeMarqueeSectionProps,
  homePageData,
  homeV2Data,
  navbarData,
  footerData,
  topBannerData,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { countryCode } = useDataStore();
  const finalCountryCode = countryCode || cookieCountryCode;
  const isIndia = finalCountryCode === "IN";
  const isUSA = finalCountryCode === "US";

  // en/us locale → SSR-rendered new V2 homepage.
  if ((locale === "en" || locale === "us") && homeV2Data) {
    return (
      <HomeV2
        data={homeV2Data}
        navbarData={navbarData}
        footerData={footerData}
        marqueeProps={homeMarqueeSectionProps}
        countryCode={finalCountryCode}
      />
    );
  }

  // homePageData is null only when the V2 early-return above should have
  // fired (en/us). If it didn't and we still got null here, render
  // nothing rather than crash on the destructure.
  if (!homePageData) return null;

  const {
    seoData,
    homeHeroSectionProps,
    homeInfoAccordionSectionProps,
    graphyAssistProps,
    featuresGridProps,
    productStatisticsProps,
    homeBottomBannerProps,
  } = homePageData;

  if (isIndia) {
    productStatisticsProps["isIndia"] = true;
  } else {
    productStatisticsProps["isIndia"] = false;
  }

  const homeTopBannerData = topBannerData?.bannerData;

  return (
    <>
      {isUSA && homeTopBannerData?.isShowBanner ? (
        <div className="flex h-full md:h-12 w-full">
          <TopBanner homeTopBannerData={homeTopBannerData} />
        </div>
      ) : null}

      <GraphyHead {...seoData} countryCode={finalCountryCode} />
      <Head>
        <meta
          name="naver-site-verification"
          content="6501b1d7fd9fb966bf1fb46e57f090f383564a6a"
        />
      </Head>
      <Navbar navbarData={navbarData} />
      <div className="flex flex-col gap-[10rem] sm:gap-[12rem] md:gap-[80px] lg:gap-12">
        <Hero {...homeHeroSectionProps} />
        <div className="mt-8 md:mt-0">
          <InfoAccordion {...homeInfoAccordionSectionProps} />
        </div>
        <GraphyAssist {...graphyAssistProps} />
        <FeaturesGrid {...featuresGridProps} />
        <ProductStatistics
          {...productStatisticsProps}
          countryCode={finalCountryCode}
        />
        <HomeMarqueeSection {...homeMarqueeSectionProps} />
        <HomeBottomBanner {...homeBottomBannerProps} />
      </div>
      <Footer footerData={footerData} />
    </>
  );
}
